/**
 * Application logging helpers (console-based).
 * Use `sendAppLog` / `captureAppException` / `buildLogContext` / `LOG_LEVEL`.
 */

export type LogAttributes = Record<string, string | number | boolean>;

export enum LOG_LEVEL {
  TRACE = "TRACE",
  DEBUG = "DEBUG",
  INFO = "INFO",
  WARNING = "WARNING",
  ERROR = "ERROR",
  FATAL = "FATAL"
}

/**
 * Flat context for structured console logs (module / operation / ids).
 */
export const buildLogContext = (
  module: string,
  operation: string,
  userId?: string | number,
  companyId?: string | number,
  storeId?: string | number,
  extra?: LogAttributes
): LogAttributes => ({
  module,
  operation,
  ...(userId !== undefined && { userId: String(userId) }),
  ...(companyId !== undefined && { companyId: String(companyId) }),
  ...(storeId !== undefined && { storeId: String(storeId) }),
  ...(extra ?? {})
});

const getBrowserContext = (): LogAttributes => {
  if (typeof window === "undefined") return {};
  return {
    path_name: window.location.pathname,
    origin: window.location.origin,
    href: window.location.href
  };
};

const buildAttributes = (context?: LogAttributes): LogAttributes => ({
  ...getBrowserContext(),
  ...(context ?? {})
});

/**
 * Structured operational log (info / warning / debug, etc.).
 */
export const sendAppLog = (
  logLevel: LOG_LEVEL,
  logMessage: string,
  context?: LogAttributes
): void => {
  const attrs = buildAttributes(context);
  switch (logLevel) {
    case LOG_LEVEL.TRACE:
    case LOG_LEVEL.DEBUG:
      console.debug(logMessage, attrs);
      break;
    case LOG_LEVEL.INFO:
      console.info(logMessage, attrs);
      break;
    case LOG_LEVEL.WARNING:
      console.warn(logMessage, attrs);
      break;
    case LOG_LEVEL.ERROR:
    case LOG_LEVEL.FATAL:
      console.error(logMessage, attrs);
      break;
    default:
      console.info(logMessage, attrs);
  }
};

/**
 * Log an unexpected failure with stack-friendly console.error.
 * Signature matches the optional errorHandler passed to fetchGraphQL.
 */
export const captureAppException = (
  exceptionName: string,
  error: unknown,
  context?: LogAttributes
): void => {
  const errorMessage =
    error instanceof Error ? error.message : String(error ?? "unknown");
  const errorName = error instanceof Error ? error.name : "UnknownError";
  const attrs = buildAttributes({
    ...(context ?? {}),
    error_name: errorName,
    error_message: errorMessage
  });
  console.error(`${exceptionName}: ${errorMessage}`, attrs, error);
};
